/* eslint-disable max-lines */
import { Selectable } from '@hakimo-ui/shared/ui-base';
import { MotionAlarm, StatusType } from './alarms/alarms';
import { Location } from './location';
import { UnifiedMonitoringProduct } from './unified-monitoring';

export interface LocationAlarmsDTO {
  total: number;
  items: LocationAlarm[];
}

export interface ABTestConfig {
  [key: string]: boolean;
}

/** How an alarm was closed out. Shared by location alarms and unified groups. */
export type AlarmResolution =
  | 'open'
  | 'safe'
  | 'escalation_open'
  | 'escalation_close';

export interface LocationAlarm {
  id: string;
  // v3 only: the source group this alarm belongs to (camera group, door, or an
  // alarm-derived fallback), from the alarm root -- never derived from a camera
  // or the camera-groups table. Escalations are scoped to it. Absent on v2.
  sourceGroupId?: string;
  // The alarm type's id, not the display name in `locationAlarmType`, which can
  // be renamed or collide. This is what incident scoping compares against.
  locationAlarmTypeId?: string;
  guid: string;
  alarmTime: string;
  status: LocationAlarmStatus;
  location: Location;
  tap: number;
  rawAlarms: MotionAlarm[];
  rawAlarmsCount: number;
  locationIncidents?: LocationAlarmIncidentDTO[];
  recommendation?: string;
  resolution?: AlarmResolution;
  abTests?: ABTestConfig;
  locationAlarmType?: string;
  // ai-engine decides enterprise-ness per tenant and reports it on the alarm.
  // Optional until that ships; absent means treat it as remote guarding.
  product?: UnifiedMonitoringProduct;
}
export const locationAlarmStatuses = [
  'Analyzing',
  'Pending',
  'Resolved',
  'In Progress',
] as const;

export type LocationAlarmStatus = (typeof locationAlarmStatuses)[number];

export enum TwilioLogType {
  TWILIO_CALLS = 'Twilio calls',
  Twilio_SMS = 'Twilio sms',
}

export enum IncidentRspndrType {
  ALARM_RESPONSE = 'ALARM RESPONSE',
  PATROL_CHECK = 'PATROL CHECK',
  DIY = 'DIY',
  UNKNOWN = 'UNKNOWN',
}

export interface LocationAlarmUpdateRequestDTO {
  comment?: string;
  status?: StatusType;
  twilio_log_type?: TwilioLogType;
  twilio_ssid?: string;
  twilio_calling_to_name?: string;
}

export enum TalkdownStatus {
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}

export enum TalkdownType {
  MANUAL = 'manual',
  AUTOMATED = 'automated',
}

export interface AlarmTalkdownState {
  id: string;
  custom_message: string;
  ingested_talkdown_audio: string;
  location_alarm_id: string;
  raw_alarm_id?: string;
  status: TalkdownStatus;
  talkdown_audio: string;
  talkdown_completed_utc: string;
  talkdown_state: string;
  talkdown_type: TalkdownType;
}
export interface LocationAlarmUpdatesDTO {
  items: LocationAlarmUpdate[];
  talkdowns?: AlarmTalkdownState[];
  incident_description?: string | null;
}

export enum ScanAlarmUpdateType {
  DETECTION_EVENT = 'detectionevent',
  ESCALATION_OPEN = 'escalationopen',
  ESCALATION_CLOSED = 'escalationclosed',
  ORPHAN = 'orphan',
  RESOLVED = 'resolved',
  ADD_EVENT = 'Add new event',
  ALLOCATION = 'allocation',
  LLM_GROUP_EVENT = 'Add AI comment',
  AUTORESOLVED = 'Auto resolved',
  VIEW_ALARM = 'View alarm',
  LLM_EVENT = 'llmevent',
  STATUS_UPDATE = 'statusupdate',
  ADD_EVENT_RAW = 'Added to alarm group',
  RSPNDR = 'Rspndr',
  DISPATCH = 'dispatch',
  ARRIVAL = 'arrival',
  COMMENT_FOR_SNAPSHOT = 'comment_for_snapshot',
}
export interface LocationAlarmUpdate {
  id: number | string;
  update_status: LocationAlarmStatus;
  update_text: string;
  update_time: string;
  update_type: LocationAlarmUpdateType | ScanAlarmUpdateType;
  user?: Selectable;
  audio_url?: string;
  twilio_log_type?: TwilioLogType;
  twilio_ssid?: string;
  twilio_calling_to_name?: string;
  raw_alarm_id?: string;
  event_id?: string;
  operator_id?: string;
  escalation_comment?: string;
  end_time_utc?: string;
  resolution_comment?: string;
  resolution_reason?: string;
  incident_category?: string;
  recommendation?: string;
  summary?: string;
  start_time_utc?: string;
  is_situation_editing_enabled?: boolean;
  situation?: string;
  situation_description?: string;
  s3_urls?: string[];
  dispatch_status?: string;
  responder_type?: string;
  notes?: string;
  comment?: string;
  event_time_utc?: string;
  /** Event-details timestamp (epoch ms or ISO); preferred over update_time when present */
  timestamp_utc?: string | number;
}

export enum LocationAlarmUpdateType {
  ADD_EVENT = 'Add new event',
  CHANGE_STATUS = 'Change Status',
  UPDATE_TAP = 'Update TAP',
  ADD_COMMENT = 'Add comment',
  VIEW_ALARM = 'View alarm',
  MANUAL_TALKDOWN = 'Manual audio talkdown',
  CALL_SMS_LOGS = 'Call SMS logs',
  ALLOCATION = 'Allocation',
  ORPHAN = 'Orphan',
  ESCALATION_OPEN = 'EscalationOpen',
  ESCALATION_SNAPSHOT_COMMENT = 'Snapshot and Comment added',
  DISPATCH = 'Dispatch',
  ARRIVAL = 'Arrival',
  CALL_EVENT = 'Call event',
}

export interface PartialLocationAlarmIncidentDTO {
  location_id?: number;
  tenant_id?: string;
  title?: string;
  description?: string;
  created_by?: string;
  resolved_by?: string;
}
export interface LocationAlarmIncidentDTO
  extends PartialLocationAlarmIncidentDTO {
  id?: number;
  incidentStartTimeUtc?: string;
  incidentEndTimeUtc?: string;
  // The alarm the incident is anchored to, not necessarily the alarm this DTO
  // arrived on: a repeat escalation in the same source group returns the
  // group's existing incident.
  initiatingLocationAlarmId?: string;
  // v3 only. `locationIncidents` carries every incident at the location, so
  // these are the scoping keys the UI matches against the alarm's own.
  sourceGroupId?: string;
  // Taken from the alarm the incident was opened on, for every product.
  locationAlarmTypeId?: string;
}
export interface UpsertLocationAlarmIncidentDTO
  extends PartialLocationAlarmIncidentDTO {
  incident_id?: number;
  location_alarm_id?: string;
  // Required by the v3 create (omitting it is a 400); ignored by the legacy v2
  // controller, which stays location-scoped.
  source_group_id?: string;
  // The product the alarm belongs to. Informational: one active incident per
  // (source group, alarm type) applies to every product.
  product?: UnifiedMonitoringProduct;
  resolution_comment?: string;
  resolution_reason?: string;
  incident_category?: string;
}

/**
 * Response of `POST /v2/orm/location_incidents/` on the v3 path. When the group
 * already has an active incident the backend returns it unchanged, so
 * `location_alarm_id` is the group's canonical anchor and may name a different
 * alarm than the one escalated from.
 */
export interface UpsertLocationAlarmIncidentResponseDTO {
  status?: number;
  message?: string;
  incident_id?: number;
  location_alarm_id?: string;
  source_group_id?: string;
}

export interface TwilioCalDetailsDTO {
  callDuration: string;
  callFrom?: string;
  callTo: string;
  endTime: number;
  startTime: number;
  mediaUrl: string;
}

export interface TwilioSMSDetailsDTO {
  smsTo: string;
  smsBody: string;
  smsStatus: string;
}

export interface CallTimeRangeProps {
  twilioSsid: string;
  fallbackTime: string;
  locationTimezone?: string;
}

export interface CallTranscriptResponse {
  transcript: string;
}

export type LocationAlarmCamera = {
  id: string;
  isTalkdownEnabled: boolean;
  name?: string;
  livestreamUrl?: string;
};

export enum RspndrCallType {
  MOBILE = 'MOBILE',
}

export interface RspndrCallListItem {
  order: number;
  name: string;
  type: RspndrCallType;
  number: string;
}

export interface Rspndr {
  escalationType?: string;
  tenantId: string;
  locationId: number;
  address1: string;
  name?: string;
  city: string;
  state: string;
  postal_code?: string;
  country: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  incident_id?: string;
  notes?: string;
  type?: IncidentRspndrType;
  call_list?: RspndrCallListItem[];
}

export interface RspndrCancel {
  incident_id?: string;
  notes?: string;
}

export interface RspndrNotify {
  incident_id?: string;
  message?: string;
}

export interface ModifyAlarmUpdateEntry {
  alarm_update_id: string;
  update_timestamp_utc?: string;
  update_type?:
    | ScanAlarmUpdateType.DISPATCH
    | ScanAlarmUpdateType.ARRIVAL
    | ScanAlarmUpdateType.COMMENT_FOR_SNAPSHOT
    | LocationAlarmUpdateType.DISPATCH
    | LocationAlarmUpdateType.ARRIVAL
    | LocationAlarmUpdateType.ESCALATION_SNAPSHOT_COMMENT;
  dispatch_status?: 'ACCEPTED' | 'CANCELLED' | 'DENIED';
  responder_type?: 'POLICE' | 'SECURITY';
  comment?: string;
  s3_urls?: string[];
}
